import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TicketIcon,
  ArrowsRightLeftIcon,
  TrashIcon,
  CheckBadgeIcon,
  ArrowDownTrayIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

const API_URL = '/api/v1';
const int = (val) => Math.floor(parseFloat(val));

const mapSource = (code) => {
  const map = {
    'SOLAR': 'Solar PV',
    'WIND': 'Wind Offshore',
    'BIOMASS': 'Biomass',
    'F01010101': 'Solar Fuel',
    'T010000': 'Solar Energy',
    'T020000': 'Wind Energy',
    'T030000': 'Hydro Energy',
  };
  return map[code] || code;
};

const statusBadge = (status) => {
  const map = {
    ISSUED: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    PENDING_ISSUANCE: 'bg-amber-50 border-amber-200 text-amber-700',
    TRANSFERRED: 'bg-blue-50 border-blue-200 text-blue-700',
    CANCELLED: 'bg-red-50 border-red-200 text-red-700',
    EXPIRED: 'bg-slate-100 border-slate-300 text-slate-400',
  };
  return map[status] || 'bg-slate-50 border-slate-200 text-slate-700';
};

const usageBadge = (usage) => {
  const map = {
    SELF_CONSUMPTION: 'bg-indigo-50 border-indigo-100 text-indigo-700',
    GRID_INJECTED: 'bg-blue-50 border-blue-100 text-blue-700',
  };
  return map[usage] || 'bg-slate-50 border-slate-100 text-slate-700';
};

const CertificateCard = ({ cert }) => {
  const handleDownloadCertificate = async (e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API_URL}/certificates/${cert.certificate_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `Certificate_${cert.gs1_id}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { alert('Download failed'); }
  };

  const handleDownloadStatement = async (e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API_URL}/reporting/cancellation-statement/${cert.certificate_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `Statement_${cert.gs1_id}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { alert('Download failed'); }
  };

  const isExpired = cert.status === 'EXPIRED';
  const isPending = cert.status === 'PENDING_ISSUANCE';

  return (
    <div className={`p-5 sm:p-6 rounded-2xl border-l-4 ${isPending ? 'border-l-amber-500' : 'border-l-[#155e75]'} animate-fade-in bg-white border border-[#adc6ce]/30 shadow-sm relative overflow-hidden group`}>

      <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6 relative z-10">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className={`w-12 h-12 ${isPending ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-[#155e75]'} rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105`}>
            <TicketIcon className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{i18n.t('gs1_giai') || 'GS1 GIAI'}</p>
              <span className={`px-2 py-0.5 rounded-full text-[8px] font-bold border ${usageBadge(cert.usage_category)}`}>
                {i18n.t(`usage_${cert.usage_category.toLowerCase()}`) || cert.usage_category}
              </span>
            </div>
            <p className="text-xs sm:text-sm font-mono text-slate-800 tracking-tight font-bold break-all">{cert.gs1_id || 'Generating...'}</p>
            <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-tight mt-1">{cert.device_name || 'Verified Plant'}</p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-2">
              <p className="text-[10px] sm:text-[11px] font-mono text-[#155e75] font-bold underline underline-offset-4 decoration-1 decoration-blue-200">
                Range: [{int(cert.start_serial)} — {int(cert.end_serial)}]
              </p>
              <div className="hidden sm:block w-1 h-1 bg-slate-300 rounded-full" />
              <p className="text-[9px] font-bold text-slate-400 uppercase">
                {cert.verification_seal_text ? i18n.t('verified_by') + ' ' + cert.verification_seal_text.split(': ')[1] : i18n.t('verified_by') + ' ONEE'}
              </p>
            </div>
          </div>
        </div>

        <span className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border flex-shrink-0 ${statusBadge(cert.status)}`}>
          {i18n.t(`status_${cert.status.toLowerCase()}`) || cert.status}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6 py-4 border-t border-slate-100 relative z-10">
        <div><p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Source</p><p className="text-xs font-bold text-[#155e75]">{mapSource(cert.energy_source_code)}</p></div>
        <div><p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Face Value</p><p className="text-xs font-bold text-slate-800">1.000 MWh</p></div>
        <div><p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Batch Total</p><p className="text-xs sm:text-sm font-extrabold text-slate-800">{cert.output_mwh} MWh</p></div>
        <div className="col-span-2 sm:col-span-1">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">{i18n.t('expiry_red_ii')}</p>
          <p className="text-xs font-bold text-red-600 leading-tight">
            {new Date(cert.expiry_date).toLocaleDateString()}<br />
            <span className="text-[9px] text-slate-400 font-semibold">{i18n.t('full_disclosure')}: {cert.disclosure_expiry_date ? new Date(cert.disclosure_expiry_date).toLocaleDateString() : 'N/A'}</span>
          </p>
        </div>
        <div><p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Issuance</p><p className="text-xs font-bold text-slate-500">{new Date(cert.issue_date).toLocaleDateString()}</p></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mt-6 pt-4 border-t border-slate-100 relative z-10">
        <div className="flex items-center gap-2">
          <CheckBadgeIcon className={`w-4 h-4 ${isPending ? 'text-amber-400' : 'text-emerald-600'}`} />
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            {isPending ? i18n.t('administrative_minting') : 'EECS v8 Validated'}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {cert.status === 'ISSUED' && (
            <>
              <button
                onClick={() => window.location.href = `/transfers/new?certId=${cert.certificate_id}`}
                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-[#adc6ce]/60 rounded-xl text-[10px] font-bold uppercase tracking-wider text-slate-600 hover:bg-[#155e75] hover:text-white transition-all shadow-sm flex-1 sm:flex-none"
              >
                <ArrowsRightLeftIcon className="w-3.5 h-3.5" /> {i18n.t('transfer_btn')}
              </button>
              <button
                onClick={() => window.location.href = `/certificates/cancel?certId=${cert.certificate_id}`}
                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-red-200 rounded-xl text-[10px] font-bold uppercase tracking-wider text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all shadow-sm flex-1 sm:flex-none"
              >
                <TrashIcon className="w-3.5 h-3.5" /> {i18n.t('cancel_btn')}
              </button>
              <button
                onClick={handleDownloadCertificate}
                className="flex items-center justify-center gap-1.5 px-4 py-2 bg-[#155e75] text-white text-[10px] font-bold uppercase tracking-wider rounded-xl hover:bg-[#0e4e61] transition-all shadow-md w-full sm:w-auto"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" /> PDF
              </button>
            </>
          )}
          {cert.status === 'CANCELLED' && (
            <button
              onClick={handleDownloadStatement}
              className="flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-xl hover:bg-emerald-700 transition-all shadow-md w-full sm:w-auto"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Statement (EN/FR)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


const CertificateTable = ({ certificates }) => (
  <div className="bg-white border border-[#adc6ce]/30 rounded-2xl shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="ent-table w-full text-xs text-left">
        <thead>
          <tr className="bg-slate-50/50 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
            <th className="py-4 px-5">GS1 GIAI</th>
            <th className="py-4">Production Plant</th>
            <th className="py-4">Type</th>
            <th className="py-4">Range</th>
            <th className="py-4">Status</th>
            <th className="py-4">Source</th>
            <th className="py-4 text-right px-5">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {certificates.map(cert => (
            <tr key={cert.certificate_id} className="hover:bg-slate-50/20 transition-colors">
              <td className="py-4 px-5">
                <p className="font-mono text-[10px] font-bold text-slate-700">{cert.gs1_id}</p>
              </td>
              <td className="py-4 text-[10px] text-indigo-600 font-bold uppercase tracking-tight">
                {cert.device_name || 'Verified Asset'}
              </td>
              <td className="py-4">
                <span className={`px-2 py-0.5 rounded-full text-[8px] font-bold uppercase border ${usageBadge(cert.usage_category)}`}>
                  {i18n.t(`usage_${cert.usage_category.toLowerCase()}`)?.split(' ')[0]}
                </span>
              </td>
              <td className="py-4 font-mono text-[10px] text-[#155e75] font-bold">{int(cert.start_serial)}–{int(cert.end_serial)}</td>
              <td className="py-4">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${statusBadge(cert.status)}`}>
                  {i18n.t(`status_${cert.status.toLowerCase()}`) || cert.status}
                </span>
              </td>
              <td className="py-4 text-[10px] text-slate-600 font-bold">{mapSource(cert.energy_source_code)}</td>
              <td className="py-4 text-right px-5">
                <div className="flex gap-1.5 justify-end">
                  {cert.status === 'ISSUED' && (
                    <>
                      <button onClick={() => window.location.href = `/transfers/new?certId=${cert.certificate_id}`} className="px-2.5 py-1.5 bg-[#155e75] text-white text-[9px] font-bold uppercase rounded-lg hover:bg-[#0e4e61] transition-all">{i18n.t('transfer_btn')}</button>
                      <button onClick={() => window.location.href = `/certificates/cancel?certId=${cert.certificate_id}`} className="px-2.5 py-1.5 bg-red-500 text-white text-[9px] font-bold uppercase rounded-lg hover:bg-red-600 transition-all">{i18n.t('cancel_btn')}</button>
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `${API_URL}/certificates/${cert.certificate_id}/pdf`;
                          link.setAttribute('download', `GO_${cert.gs1_id}.pdf`);
                          document.body.appendChild(link); link.click(); link.remove();
                        }}
                        className="p-1.5 bg-slate-50 border border-slate-200 text-slate-600 text-[9px] font-bold uppercase rounded-lg hover:bg-slate-100 transition-all"
                      >
                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {cert.status === 'PENDING_ISSUANCE' && (
                    <span className="text-[8px] font-bold text-amber-600 uppercase italic">{i18n.t('awaiting_mtedd')}</span>
                  )}
                  {cert.status === 'CANCELLED' && (
                    <button
                      onClick={() => window.open(`${API_URL}/reporting/cancellation-statement/${cert.certificate_id}`, '_blank')}
                      className="px-2.5 py-1.5 bg-emerald-600 text-white text-[9px] font-bold uppercase rounded-lg hover:bg-emerald-700 transition-all flex items-center gap-1"
                    >
                      <ArrowDownTrayIcon className="w-3 h-3" /> PDF
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {certificates.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-16 text-slate-400 text-xs font-bold uppercase">
                {i18n.t('wallet_empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);


export default function Certificates() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/certificates`)
      .then(r => setCertificates(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#155e75]" />
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in max-w-6xl mx-auto py-4 sm:py-6 px-2 sm:px-4 md:px-0">


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight uppercase">
            {i18n.t('certificates')}
          </h1>
          <p className="text-xs text-slate-500 font-semibold tracking-wider uppercase mt-1">
            {i18n.t('subtitle')}
          </p>
        </div>

        <div className="flex flex-col xs:flex-row items-stretch xs:items-center justify-center gap-3">

          <div className="flex border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white self-center">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2.5 flex items-center justify-center gap-1.5 text-xs font-bold transition-all ${viewMode === 'card' ? 'bg-[#155e75] text-white' : 'bg-white text-slate-400 hover:bg-slate-50'}`}
              title="Card View"
            >
              <Squares2X2Icon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2.5 flex items-center justify-center gap-1.5 text-xs font-bold transition-all ${viewMode === 'table' ? 'bg-[#155e75] text-white' : 'bg-white text-slate-400 hover:bg-slate-50'}`}
              title="Table View"
            >
              <TableCellsIcon className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={() => window.location.href = '/issuance/request'}
            className="bg-[#155e75] hover:bg-[#0e4e61] text-white text-xs font-bold uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-lg shadow-[#155e75]/10 transition-all text-center"
          >
            {i18n.t('req_issuance')}
          </button>
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 gap-4">
          {certificates.map(cert => <CertificateCard key={cert.certificate_id} cert={cert} />)}
          {certificates.length === 0 && (
            <div className="p-10 sm:p-20 border border-dashed border-[#adc6ce]/60 rounded-2xl text-center flex flex-col items-center bg-white shadow-sm">
              <TicketIcon className="w-12 h-12 text-slate-200 mb-6" />
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Electronic Wallet Empty</h3>
              <p className="text-xs sm:text-sm text-slate-500 max-w-xs mx-auto mb-6 leading-relaxed font-semibold">No digital certificates found in the current account ledger.</p>
              <button
                onClick={() => window.location.href = '/issuance/request'}
                className="text-[#155e75] hover:text-[#0e4e61] font-bold text-xs uppercase tracking-wider hover:underline"
              >
                Submit your first issuance request →
              </button>
            </div>
          )}
        </div>
      ) : (
        <CertificateTable certificates={certificates} />
      )}
    </div>
  );
}